import React from 'react';
import PropTypes from 'prop-types';
import Cookies from 'cookies-js';
import styled from 'styled-components';
import { VerticalNav } from 'shared-assets/components/Navs';
import { A } from 'shared-assets/components/Elements';
import {
  billerPermissionsNavBarLinks,
  careChecklistPermissionsNavBarLinks,
  careTeamPermissionsNavBarLinks,
  chartPermissionsNavBarLinks,
  COMPOSITIONS_NOTES_LIST_URL,
  documentsPermissionsNavBarLinks,
  FEATURE_FLAG_COMPOSITIONS_NOTES_LIST,
  flowsheetsPermissionsNavBarLinks,
  labsPermissionsNavBarLinks,
  notesPermissionsNavBarLinks,
  recallPermissionsNavBarLinks,
} from 'shared-assets/facesheet-ui/constants';
import kfetch from '@kareo/kfetch';
import update from 'immutability-helper';
import rollbar from 'rollbar';
import Loader from 'shared-assets/components/Loader';
import lodash from 'lodash';
import PatientInfoWidget from 'shared-assets/facesheet-ui/components/ChartNavBar/PatientInfoWidget';
import { Box, palette, theme } from '@kareo/design-system';
import {
  fetchUserPermissions,
  getUserChartAccess,
} from 'shared-assets/facesheet-ui/service/accessControl/service';
import { fetchPracticePermissions } from 'shared-assets/facesheet-ui/service/facesheet/service';
import FacesheetNavBar from 'shared-assets/facesheet-ui/components/ChartNavBar/FacesheetNavBar';
import PatientMessagesNavItem from 'shared-assets/facesheet-ui/components/ChartNavBar/PatientMessagesNavItem'; // can't use "shared-assets" as its not recognised in legacy app
import featureToggles from 'shared-assets/lib/sdk/featureToggles';
import { UserChartAccess } from 'shared-assets/facesheet-ui/service/accessControl/types';

const { sortBy, intersectionBy } = lodash.noConflict();

const StyledA = styled(A)`
  &&:hover {
    background-color: var(--hover-color);
  }
`;

class ChartNavBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      linksToDisplay: billerPermissionsNavBarLinks,
      protectionSettings: {},
      userChartAccess: {},
      careTeamFeatureToggleEnabled: false,
      compositionsNotesListEnabled: false,
    };
  }

  componentDidMount() {
    this.updateStyleForLegacyIframe();
    Promise.all(
      [
        fetchUserPermissions(),
        this.fetchUserChartAccess(),
        fetchPracticePermissions(),
        this.getNavBarConfiguration(),
      ].map(p => p.catch(() => undefined)),
    )
      .then(
        ([
          userPermissions,
          userChartAccess,
          practicePermissions,
          navBarConfiguration,
        ]) => {
          const sortedLinks = this.getNavBarLinksByPermissions(
            navBarConfiguration,
            practicePermissions,
            userPermissions,
            userChartAccess,
          );
          this.setState({
            linksToDisplay: update(this.state.linksToDisplay, {
              $set: sortedLinks,
            }),
            loading: false,
            protectionSettings: userPermissions.protectionSettings,
            userChartAccess,
          });
        },
      )
      .catch(e => {
        rollbar.error(e);
        this.setState({
          loading: false,
        });
      });

    // Check Care Team feature toggle
    featureToggles
      .isEnabled('dark-feature.care-team-members')
      .then(featureToggleEnabled => {
        this.setState({
          careTeamFeatureToggleEnabled: featureToggleEnabled,
        });
      });

    featureToggles
      .isEnabled(FEATURE_FLAG_COMPOSITIONS_NOTES_LIST)
      .then(featureToggleEnabled => {
        this.setState({
          compositionsNotesListEnabled: !!featureToggleEnabled,
        });
      });
  }

  getLinksToDisplay() {
    if (!this.state.compositionsNotesListEnabled) {
      return this.state.linksToDisplay;
    }

    return this.state.linksToDisplay.map(link =>
      link.linkName === 'NOTES'
        ? { ...link, url: COMPOSITIONS_NOTES_LIST_URL }
        : link,
    );
  }

  togglePatientRestriction = () => {
    this.setState(prevState => ({
      ...prevState,
      userChartAccess: {
        ...prevState.userChartAccess,
        patientRestricted: !prevState.userChartAccess.patientRestricted,
      },
    }));
  };

  async getNavBarConfiguration() {
    const navBarConfiguration = [];
    await kfetch(`/facesheet-ui/api/Facesheet/views`)
      .then(configuration => {
        const enabledLinks = configuration.links.filter(link => link.enabled);
        navBarConfiguration.push(...enabledLinks);
      })
      .catch(e => {
        rollbar.error(e);
        navBarConfiguration.push(
          ...chartPermissionsNavBarLinks.filter(link => link.canBeConfigured),
        );
        navBarConfiguration.push(...labsPermissionsNavBarLinks);
        navBarConfiguration.push(...flowsheetsPermissionsNavBarLinks);
      });
    return navBarConfiguration;
  }

  async fetchUserChartAccess() {
    const patientId = this.props.patientId || this.props.match.params.patientId;
    try {
      const userChartAccess = await getUserChartAccess(patientId);
      if (userChartAccess?.ok) {
        const chartAccessBody = await userChartAccess.json();
        return new UserChartAccess(
          chartAccessBody?.userAccess.linked,
          chartAccessBody?.practiceAccess.chartAccessRestrictionEnabled,
          chartAccessBody?.practiceAccess.clinicalDataAccessRestrictionEnabled,
          chartAccessBody?.patientRestricted,
        );
      } else {
        return null;
      }
    } catch (error) {
      rollbar.error(error);
      return null;
    }
  }

  // Check if an individual navbar link is included in the configuration array returned from facesheet-service
  validateLink(configurationArray, navBarOption) {
    if (
      configurationArray.some(link => link.linkName === navBarOption.linkName)
    ) {
      return navBarOption;
    }
    return null;
  }

  getNavBarLinksByPermissions(
    navBarConfiguration,
    practicePermissions,
    userPermissions,
    userChartAccess,
  ) {
    const navLinksArray = [...this.state.linksToDisplay];
    let chartEnabled,
      notesEnabled,
      labsEnabled,
      flowsheetsEnabled,
      careChecklistEnabled,
      recallEnabled,
      documentsEnabled = false,
      careTeamEnabled = false;

    if (
      userPermissions?.protectionSettings.isPPCEnabled === false ||
      userChartAccess?.dataRestrictionEnabled() === false ||
      userChartAccess?.userAccessLinked
    ) {
      chartEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasChartPrivilege;
      notesEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasNotesPrivilege;
      labsEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasLabsPrivilege;
      flowsheetsEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasFlowsheetsPrivilege;
      careChecklistEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasCareChecklistPrivilege;
      recallEnabled = practicePermissions.recallEnabled;
      documentsEnabled =
        userPermissions.userPrivileges.hasChartPrivilege &&
        practicePermissions.documentsEnabled;
      careTeamEnabled =
        practicePermissions.clinicalProductEnabled &&
        userPermissions.userPrivileges.hasChartPrivilege &&
        this.state.careTeamFeatureToggleEnabled;
    }

    /* Check is user has chart permissions and if they have a navbar configuration set, if so return only the
    intersection of configuration array and configurable links but also all unconfigurable links */
    if (chartEnabled) {
      const configurableLinks = [
        ...chartPermissionsNavBarLinks.filter(link => link.canBeConfigured),
      ];
      const unConfigurableLinks = [
        ...chartPermissionsNavBarLinks.filter(link => !link.canBeConfigured),
      ];
      const configuredLinks = intersectionBy(
        configurableLinks,
        navBarConfiguration,
        'linkName',
      );
      navLinksArray.push(...unConfigurableLinks.concat(configuredLinks));
    }
    if (notesEnabled) {
      navLinksArray.push(...notesPermissionsNavBarLinks);
    }
    if (labsEnabled) {
      const labLink = this.validateLink(
        navBarConfiguration,
        ...labsPermissionsNavBarLinks,
      );
      if (labLink) navLinksArray.push(labLink);
    }
    if (flowsheetsEnabled) {
      const flowsheetsLink = this.validateLink(
        navBarConfiguration,
        ...flowsheetsPermissionsNavBarLinks,
      );
      if (flowsheetsLink) navLinksArray.push(flowsheetsLink);
    }
    if (careChecklistEnabled) {
      navLinksArray.push(...careChecklistPermissionsNavBarLinks);
    }
    if (documentsEnabled) {
      navLinksArray.push(...documentsPermissionsNavBarLinks);
    }
    if (recallEnabled) {
      navLinksArray.push(...recallPermissionsNavBarLinks);
    }
    if (careTeamEnabled) {
      navLinksArray.push(...careTeamPermissionsNavBarLinks);
    }
    const finalLinks = sortBy(navLinksArray, ['displayOrder']);
    return finalLinks;
  }

  updateStyleForLegacyIframe() {
    const isTebraTheme = Cookies.get('brand') === 'cobrand-tebra';
    const backgroundColor = isTebraTheme
      ? theme.palette?.background?.default
      : palette?.background?.default;
    if (this.props.isLegacy) {
      const iframeHeader = document.querySelector('.facesheet-chrome');
      const navContainer = document.querySelector('.has-subnav');
      iframeHeader.style.display = 'none';
      navContainer.style.background = backgroundColor;
    }
  }

  render() {
    const linksToDisplay = this.getLinksToDisplay();
    return (
      <Loader condition={!this.state.loading}>
        <If condition={this.props.isLegacy}>
          <Box height="65px" />
        </If>
        <Choose>
          <When condition={this.props.isModernFacesheetDisplayed}>
            <FacesheetNavBar
              links={linksToDisplay}
              patientId={this.props.patientId}
            />
          </When>
          <Otherwise>
            <VerticalNav
              items={[
                <If
                  condition={this.props.displayWidget && this.props.patientId}
                >
                  <PatientInfoWidget
                    patientId={this.props.patientId}
                    protectionSettings={this.state.protectionSettings}
                    userChartAccess={this.state.userChartAccess}
                    togglePatientRestriction={this.togglePatientRestriction}
                  />
                </If>,
                <For each="navBarLink" of={linksToDisplay} index="i">
                  <StyledA
                    key={i}
                    href={navBarLink.url.replace(
                      '{patientId}',
                      this.props.patientId || this.props.match.params.patientId,
                    )}
                    target="_top"
                  >
                    {navBarLink.displayName}
                  </StyledA>
                </For>,
                <If
                  condition={
                    window.location === window.parent.location // only show if nav is not in an iframe (dialog would not display correctly)
                  }
                >
                  <PatientMessagesNavItem patientId={this.props.patientId} />
                </If>,
              ]}
              style={{ width: '12rem' }}
            />
          </Otherwise>
        </Choose>
      </Loader>
    );
  }
}
ChartNavBar.propTypes = {
  patientId: PropTypes.number,
  displayWidget: PropTypes.bool,
  isModernFacesheetDisplayed: PropTypes.bool,
  isLegacy: PropTypes.bool,
};
ChartNavBar.defaultProps = {
  displayWidget: false,
  isModernFacesheetDisplayed: false,
  isLegacy: false,
};

export default ChartNavBar;
